import { ArrowRight, Check, Paperclip, Send } from "lucide-react";
import type { ReactNode } from "react";

import { Logo } from "@/components/brand/Logo";

/**
 * Marketing preview of the session workspace — mirrors the
 * "Inside the workspace" section of the reference. Uses a sample
 * BCBS / cardiac-stress-test case so visitors can see what the
 * working surface looks like before they upload anything.
 */
export function SessionPreview() {
  return (
    <section className="mx-auto max-w-[1180px] px-6 py-20 sm:px-12 sm:py-24">
      <div className="mb-10 max-w-[640px]">
        <span className="eyebrow">Inside the workspace</span>
        <h2 className="mt-6 font-serif text-[clamp(1.75rem,3.5vw,2.25rem)] font-normal leading-[1.15] tracking-[-0.02em] text-ink">
          A workspace shaped like a case file, not a chat window.
        </h2>
        <p className="mt-4 text-[15px] leading-[1.65] text-ink-soft">
          Your journey on the left, your case at center, the conversation on the right. Every
          drafted sentence is tied to a citable source — and you control what gets sent.
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-line bg-parchment">
        <AppBar />
        <div className="grid min-h-[620px] grid-cols-1 lg:grid-cols-[240px_1fr_320px]">
          <LeftRail />
          <CenterStage />
          <RightChat />
        </div>
      </div>
    </section>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────── */

function AppBar() {
  return (
    <header className="flex items-center justify-between border-b border-line bg-surface px-6 py-3.5">
      <div className="flex items-center gap-6">
        <span className="inline-flex items-center gap-2.5 font-serif text-[15px] text-ink">
          <Logo size={20} />
          Revetra
        </span>
        <span className="hidden items-center gap-2 text-[11px] text-ink-muted sm:inline-flex">
          <span>Case</span>
          <code className="case-id-code">LBZWSU</code>
        </span>
      </div>
      <div className="flex items-center gap-3.5">
        <span className="session-status hidden sm:inline-flex">
          Session active · auto-purges in 24h
        </span>
        <span className="rounded-full bg-ink px-4 py-1.5 text-[12px] font-medium text-parchment">
          Export
        </span>
      </div>
    </header>
  );
}

function LeftRail() {
  return (
    <aside className="border-b border-line bg-parchment-deep px-5 py-6 lg:border-b-0 lg:border-r">
      <div className="mb-4 text-[10px] uppercase tracking-[0.14em] text-ink-hint">
        Your appeal journey
      </div>
      <div className="journey">
        <Step status="done" label="Denial analyzed" meta="Carrier, reason, deadline extracted" />
        <Step status="done" label="Probability scored" meta="55% high confidence" />
        <Step
          status="active"
          label="Drafting appeal letter"
          meta="Section 3 of 6 · in progress"
        />
        <Step status="future" label="Citations verified" meta="Awaiting draft" />
        <Step status="future" label="Ready to send" meta="PDF + mailing instructions" />
      </div>

      <div className="my-7 border-t border-line" />

      <div className="mb-4 text-[10px] uppercase tracking-[0.14em] text-ink-hint">
        Specialists on your case
      </div>
      <div className="flex items-center">
        <span className="specialist specialist--dd">DD</span>
        <span className="specialist specialist--pr">PR</span>
        <span className="specialist specialist--mr">MR</span>
        <span className="specialist specialist--ld">LD</span>
        <span className="specialist specialist--more">+2</span>
      </div>
      <p className="mt-2.5 text-[11px] leading-[1.5] text-ink-muted">
        Six AI specialists working your case in sequence.
      </p>
    </aside>
  );
}

function Step({
  status,
  label,
  meta,
}: {
  status: "done" | "active" | "future";
  label: string;
  meta: string;
}) {
  return (
    <div className={`journey__step journey__step--${status}`}>
      <span className="journey__dot">
        {status === "done" && <Check size={9} strokeWidth={3} />}
      </span>
      <div className="journey__label">{label}</div>
      <div className="journey__meta">{meta}</div>
    </div>
  );
}

function CenterStage() {
  return (
    <main className="overflow-y-auto bg-parchment p-8">
      <div className="mb-5 flex items-baseline justify-between">
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-ink-hint">
            Carrier · BCBS of California
          </div>
          <h3 className="font-serif text-[26px] font-normal tracking-[-0.015em] text-ink">
            Cardiac stress test, CPT 93351
          </h3>
        </div>
        <div className="text-right">
          <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-ink-hint">
            Deadline
          </div>
          <div className="font-serif text-[15px] text-ink">
            Sep 17, 2026 · <span className="text-brick">121 days</span>
          </div>
        </div>
      </div>

      {/* Probability card */}
      <div className="mb-5 rounded-md border border-line bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-[48px] font-normal leading-none tracking-[-0.02em] text-ink">
              55<span className="text-[30px] text-[#C0BFB8]">%</span>
            </span>
            <div>
              <div className="text-[13px] font-medium text-ink">Probability of overturn</div>
              <div className="text-[11px] text-ink-muted">
                High confidence · industry baseline 45%
              </div>
            </div>
          </div>
          <span className="pill-verified">Strong case</span>
        </div>

        <div
          className="mb-4 flex h-1.5 overflow-hidden rounded-full bg-highlight"
          role="img"
          aria-label="Score breakdown bar"
        >
          <div className="h-full w-[45%] bg-[#C0BFB8]" />
          <div className="h-full w-[10%] bg-forest" />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Factor name="Base appeal success" value="+45 pp" />
          <Factor name="Medical necessity denial" value="+10 pp" />
        </div>
      </div>

      {/* Drafted excerpt */}
      <div className="rounded-md border border-line bg-surface p-6">
        <div className="mb-3.5 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.14em] text-ink-hint">
            Drafted excerpt · Section 03
          </span>
          <span className="text-[11px] text-ink-muted">Clinical presentation</span>
        </div>

        <p className="mb-4 font-serif text-[14.5px] leading-[1.75] text-ink">
          The patient is a <span className="redact">50-year-old male</span> who presented to
          cardiology outpatient services on <span className="redact">February 23, 2026</span>,
          with substernal chest pressure triggered by moderate exertion. Relevant past medical
          history includes hypertension controlled on lisinopril<sup className="cite">[1]</sup>,
          hyperlipidemia with a recent LDL of 142 mg/dL, and a strong family history of premature
          coronary artery disease<sup className="cite">[2]</sup>.
        </p>

        <div className="flex flex-wrap items-center gap-2.5 border-t border-line pt-3.5">
          <span className="text-[11px] text-ink-muted">Sources:</span>
          <span className="source-chip">
            <span className="source-chip__num">[1]</span>Patient chart, pg 4
          </span>
          <span className="source-chip">
            <span className="source-chip__num">[2]</span>Patient chart, pg 7
          </span>
        </div>
      </div>
    </main>
  );
}

function Factor({ name, value }: { name: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-sm bg-parchment-soft px-3 py-2.5">
      <span className="text-[12px] text-ink-soft">{name}</span>
      <span className="text-[12px] font-medium text-forest">{value}</span>
    </div>
  );
}

function RightChat() {
  return (
    <aside className="flex flex-col border-t border-line bg-surface px-5 py-6 lg:border-l lg:border-t-0">
      <div className="mb-4 text-[10px] uppercase tracking-[0.14em] text-ink-hint">
        Conversation
      </div>

      <div className="mb-2 flex items-center gap-2 text-[11px] text-ink-muted">
        <Logo size={20} />
        Letter Drafter · just now
      </div>
      <div className="mb-6 rounded-md bg-parchment-deep px-4 py-3.5 text-[13px] leading-[1.6] text-ink">
        I&apos;ve drafted the clinical presentation section. It establishes a classic angina
        pattern with objective risk factors. Want me to refine, or move to disease progression?
      </div>

      <div className="mb-6 flex flex-col gap-2">
        <ChatAction label="Refine this section" />
        <ChatAction label="Continue to next section" primary />
        <ChatAction label="Add medical record" icon={<Paperclip size={13} strokeWidth={1.75} />} />
      </div>

      <div className="mt-auto border-t border-line pt-4">
        <div className="flex items-center gap-2.5 rounded-md bg-parchment-soft px-3.5 py-2.5">
          <input
            type="text"
            placeholder="Ask anything about your case…"
            className="flex-1 bg-transparent text-[12px] text-ink outline-none placeholder:text-ink-hint"
            readOnly
          />
          <Send size={14} strokeWidth={1.75} className="text-ink-muted" aria-hidden />
        </div>
      </div>
    </aside>
  );
}

function ChatAction({
  label,
  primary,
  icon,
}: {
  label: string;
  primary?: boolean;
  icon?: ReactNode;
}) {
  const trail = icon ?? <ArrowRight size={13} strokeWidth={1.75} />;
  if (primary) {
    return (
      <button
        type="button"
        className="flex items-center justify-between rounded-md bg-ink px-3.5 py-2.5 text-[12px] text-parchment transition hover:bg-black"
      >
        <span>{label}</span>
        <span aria-hidden>{trail}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className="flex items-center justify-between rounded-md border border-line-strong px-3.5 py-2.5 text-[12px] text-ink transition hover:bg-parchment-soft"
    >
      <span>{label}</span>
      <span aria-hidden className="text-ink-muted">
        {trail}
      </span>
    </button>
  );
}
